import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { ID } from '@shared/types';
import {
  buildBaseFields,
  buildUpdateFields,
  fromSnapshot,
  generateCode,
  nextDocumentBranchNumber,
  writeAuditLog,
} from '@shared/utils';
import {
  fetchCustomersByPartnerType,
  fetchCustomer,
  createPartner,
  updateCustomer,
  deleteCustomer,
  type CustomerUpdateInput,
} from '@modules/customer/services/customer.service';
import { updateOrderAggregates } from '@modules/order/services/order.service';
import type { Subcontractor, SubcontractOrder, SubcontractOrderStatus } from '../types';

// ─── Subcontractor（外注先）= 取引先マスタ（customers）の subcontractor 区分 ──────
// 外注先は独立コレクションではなく取引先マスタに統合。1社が顧客/仕入先/外注先を兼ねられる。

const subcontractorSchema = z.object({
  name: z.string().min(1, '外注先名は必須です'),
  email: z.string().email().nullable().default(null),
  phone: z.string().nullable().default(null),
  postalCode: z.string().nullable().default(null),
  prefecture: z.string().default(''),
  addr1: z.string().default(''),
  addr2: z.string().default(''),
  addr3: z.string().default(''),
  department: z.string().default(''),
  paymentTerms: z.number().int().min(0).default(30),
  notes: z.string().nullable().default(null),
});

export type SubcontractorCreateInput = z.infer<typeof subcontractorSchema>;
export type SubcontractorUpdateInput = Partial<SubcontractorCreateInput>;

export async function fetchSubcontractors(): Promise<Subcontractor[]> {
  return (await fetchCustomersByPartnerType('subcontractor')) as unknown as Subcontractor[];
}

export async function fetchSubcontractor(id: ID): Promise<Subcontractor | null> {
  return (await fetchCustomer(id)) as unknown as Subcontractor | null;
}

export async function createSubcontractor(
  data: SubcontractorCreateInput,
  userId: ID
): Promise<Subcontractor> {
  const created = await createPartner(data, 'subcontractor', userId);
  return created as unknown as Subcontractor;
}

export async function updateSubcontractor(
  id: ID,
  data: SubcontractorUpdateInput,
  userId: ID
): Promise<Subcontractor> {
  const updated = await updateCustomer(id, data as CustomerUpdateInput, userId);
  return updated as unknown as Subcontractor;
}

export async function deleteSubcontractor(id: ID, userId: ID): Promise<void> {
  await deleteCustomer(id, userId);
}

// ─── SubcontractOrder CRUD ─────────────────────────────────────────────────

const lineItemSchema = z.object({
  id: z.string(),
  // 品名・摘要欄に入力した行の位置を保持するため、ユーザーが意図的に空けた
  // 途中の空行（スペーサー行）も保存対象とする。空文字 title を許容する。
  title: z.string(),
  qty: z.number().min(0),
  unitId: z.string(),
  // 値引き行に対応するため負の単価・金額を許容する（Infinity/NaN は拒否）
  unitPrice: z.number().finite(),
  amount: z.number().finite(),
  taxRate: z.union([z.literal(10), z.literal(8), z.literal('exempt')]),
});

const orderSchema = z.object({
  subcontractorId: z.string().min(1, '外注先は必須です'),
  orderId: z.string().nullable().default(null),
  assignedUserId: z.string().nullable().default(null),
  title: z.string().min(1, '件名は必須です'),
  description: z.string().nullable().default(null),
  amount: z.number().min(0),
  taxAmount: z.number().min(0),
  total: z.number().min(0),
  status: z.enum(['draft', 'ordered', 'completed', 'cancelled']).default('draft'),
  orderDate: z.instanceof(Timestamp),
  deliveryDate: z.instanceof(Timestamp).nullable().default(null),
  paidAt: z.instanceof(Timestamp).nullable().default(null),
  notes: z.string().nullable().default(null),
  billId: z.string().nullable().default(null),
  paymentDate: z.instanceof(Timestamp).nullable().default(null),
  lineItems: z.array(lineItemSchema).default([]),
  subtotal: z.number().min(0).default(0),
  totalgst: z.number().min(0).finite().default(0),
  grandtotal: z.number().min(0).finite().default(0),
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  isZeroSale: z.boolean().default(false),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
});

export type SubcontractOrderCreateInput = z.infer<typeof orderSchema>;
export type SubcontractOrderUpdateInput = Partial<SubcontractOrderCreateInput>;

const ORD_COL = 'subcontract_orders';

/**
 * 案件に紐づく未削除・キャンセル以外の外注合計を再集計し、受注一覧の外注費（order.subcontractTotal）へ反映する。
 * 外注の作成・更新・削除時に呼び出し、削除後は残った外注の合計（0円含む）へ連動させる。
 */
async function syncOrderSubcontractTotal(orderId: ID | null | undefined, userId: ID): Promise<void> {
  if (!orderId) return;
  const snap = await getDocs(
    query(collection(db, ORD_COL), where('orderId', '==', orderId), where('isDeleted', '==', false))
  );
  const subcontractTotal = snap.docs.reduce((s, d) => {
    const data = d.data();
    if (data['status'] === 'cancelled') return s;
    return s + ((data['grandtotal'] as number | undefined) ?? 0);
  }, 0);
  await updateOrderAggregates(orderId, { subcontractTotal }, userId).catch(() => undefined);
}

export async function fetchSubcontractOrders(filters?: {
  subcontractorId?: ID;
  status?: SubcontractOrderStatus;
}): Promise<SubcontractOrder[]> {
  let q = query(
    collection(db, ORD_COL),
    where('isDeleted', '==', false),
    orderBy('orderDate', 'desc'),
    limit(500)
  );
  if (filters?.subcontractorId) {
    q = query(q, where('subcontractorId', '==', filters.subcontractorId));
  }
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as SubcontractOrder);
}

export async function fetchSubcontractOrder(id: ID): Promise<SubcontractOrder | null> {
  return fromSnapshot<SubcontractOrder>(await getDoc(doc(db, ORD_COL, id)));
}

export async function createSubcontractOrder(
  data: SubcontractOrderCreateInput,
  userId: ID
): Promise<SubcontractOrder> {
  const validated = parseOrThrow(orderSchema, data);
  // 案件帳票は「受注番号-枝番」(0001-01) を code に持つ。案件に紐づかない場合のみ PREFIX-00001。
  const branch = await nextDocumentBranchNumber(ORD_COL, validated.orderId ?? null);
  const code = branch ?? (await generateCode('PO'));
  const payload = { ...validated, code, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, ORD_COL), payload);
  await writeAuditLog({ entityType: 'subcontract_order', entityId: ref.id, action: 'create', userId, after: validated });
  await syncOrderSubcontractTotal(payload.orderId, userId);
  return { id: ref.id, ...payload } as unknown as SubcontractOrder;
}

export async function updateSubcontractOrder(
  id: ID,
  data: SubcontractOrderUpdateInput,
  userId: ID
): Promise<SubcontractOrder> {
  const ref = doc(db, ORD_COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('外注発注が見つかりません。');
  const before = snap.data();
  const patch = { ...data, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({ entityType: 'subcontract_order', entityId: id, action: 'update', userId, before, after: data });
  const prevOrderId = (before['orderId'] as ID | null | undefined) ?? null;
  const nextOrderId = (data.orderId ?? prevOrderId) as ID | null;
  await syncOrderSubcontractTotal(nextOrderId, userId);
  if (prevOrderId && prevOrderId !== nextOrderId) {
    await syncOrderSubcontractTotal(prevOrderId, userId);
  }
  return { id, ...before, ...patch } as unknown as SubcontractOrder;
}

export async function deleteSubcontractOrder(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, ORD_COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('外注発注が見つかりません。');
  const orderId = (snap.data()['orderId'] as ID | null | undefined) ?? null;
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({ entityType: 'subcontract_order', entityId: id, action: 'delete', userId, before: snap.data() });
  await syncOrderSubcontractTotal(orderId, userId);
}

export async function linkSubcontractToBill(
  subcontractOrderId: ID,
  billId: ID | null,
  userId: ID
): Promise<SubcontractOrder> {
  return updateSubcontractOrder(subcontractOrderId, { billId }, userId);
}

export async function fetchSubcontractorCosts(month: string): Promise<number> {
  const [year, m] = month.split('-').map(Number);
  const start = Timestamp.fromDate(new Date(year, m - 1, 1));
  const end = Timestamp.fromDate(new Date(year, m, 1));
  const q = query(
    collection(db, ORD_COL),
    where('isDeleted', '==', false),
    where('orderDate', '>=', start),
    where('orderDate', '<', end)
  );
  const snap = await getDocs(q);
  return snap.docs.reduce((sum, d) => sum + ((d.data()['total'] as number) ?? 0), 0);
}
